import { computeRunningEffectiveness, resolveAthleteWeightKg } from './runningEffectiveness';

/**
 * Running KPI cards (same metrics as activity detail KPI row).
 * @param {object} activity — strava_activities row (snake_case fields from API)
 * @param {(key: string) => string} t — i18n translate
 */
export function computeKpis(activity, t) {
  if (!activity) return [];
  const speed = parseFloat(activity.average_speed);
  const hr = parseFloat(activity.average_heartrate);
  const watts = parseFloat(activity.average_watts);
  const cadence = parseFloat(activity.average_cadence);
  const dist = parseFloat(activity.distance);
  const movTime = parseFloat(activity.moving_time);
  const elevGain = parseFloat(activity.total_elevation_gain);
  const cals = parseFloat(activity.calories);

  const hasSpeed = speed > 0;
  const hasHr = activity.has_heartrate && hr > 0;
  const hasWatts = watts > 0;
  const hasCadence = cadence > 0;
  const hasDist = dist > 0;

  const paceMinKm = hasSpeed ? 1000 / (speed * 60) : 0;
  const paceSec = paceMinKm * 60;

  const kpis = [];

  if (hasSpeed && hasWatts) {
    const weightKg = resolveAthleteWeightKg(activity);
    const re = computeRunningEffectiveness(speed, watts, weightKg);
    if (re != null) {
      kpis.push({
        key: 're',
        label: t('activityDetail.kpi.re'),
        value: re.toFixed(2),
        numericValue: re,
        unit: 'kg/N',
        color: '#10b981',
        hint: t('activityDetail.kpi.reHint'),
      });
    }
  }

  if (hasCadence && hasDist && movTime > 0) {
    const strideLen = dist / (cadence * 2 * (movTime / 60));
    kpis.push({
      key: 'stride',
      label: t('activityDetail.kpi.stride'),
      value: strideLen.toFixed(2),
      numericValue: strideLen,
      unit: 'm',
      color: '#f59e0b',
    });
  }

  if (hasSpeed && hasHr) {
    const paceBpm = paceSec / hr;
    kpis.push({
      key: 'paceBpm',
      label: t('activityDetail.kpi.paceBpm'),
      value: paceBpm.toFixed(2),
      numericValue: paceBpm,
      unit: 's·km⁻¹/bpm',
      color: '#3b82f6',
    });
  }

  if (hasHr && hasWatts) {
    const bpmWatt = hr / watts;
    kpis.push({
      key: 'bpmWatt',
      label: t('activityDetail.kpi.bpmWatt'),
      value: bpmWatt.toFixed(2),
      numericValue: bpmWatt,
      unit: 'bpm/W',
      color: '#ef4444',
    });
  }

  if (hasSpeed && hasWatts) {
    const paceWatt = paceSec / watts;
    kpis.push({
      key: 'paceWatt',
      label: t('activityDetail.kpi.paceWatt'),
      value: paceWatt.toFixed(2),
      numericValue: paceWatt,
      unit: 's·km⁻¹/W',
      color: '#a855f7',
    });
  }

  if (hasDist && elevGain > 0) {
    const elevKm = elevGain / (dist / 1000);
    kpis.push({
      key: 'elevKm',
      label: t('activityDetail.kpi.elevKm'),
      value: elevKm.toFixed(1),
      numericValue: elevKm,
      unit: 'm/km',
      color: '#6366f1',
    });
  }

  if (hasDist && cals > 0) {
    const calKm = cals / (dist / 1000);
    kpis.push({
      key: 'calKm',
      label: t('activityDetail.kpi.calKm'),
      value: calKm.toFixed(0),
      numericValue: calKm,
      unit: 'kcal/km',
      color: '#f97316',
    });
  }

  return kpis;
}

/** Formatted value for tooltips (matches card display). */
export function formatDerivedKpiDisplay(key, numericValue) {
  if (!Number.isFinite(numericValue)) return '—';
  switch (key) {
    case 're':
      return `${numericValue.toFixed(2)} kg/N`;
    case 'stride':
      return `${numericValue.toFixed(2)} m`;
    case 'paceBpm':
      return `${numericValue.toFixed(2)} s·km⁻¹/bpm`;
    case 'bpmWatt':
      return `${numericValue.toFixed(2)} bpm/W`;
    case 'paceWatt':
      return `${numericValue.toFixed(2)} s·km⁻¹/W`;
    case 'elevKm':
      return `${numericValue.toFixed(1)} m/km`;
    case 'calKm':
      return `${numericValue.toFixed(0)} kcal/km`;
    default:
      return String(numericValue);
  }
}

export function buildKpiCardValueMap(activity, t) {
  if (!activity) return {};
  const kpis = computeKpis(activity, t);
  const m = {};
  for (const k of kpis) {
    if (k.numericValue != null && Number.isFinite(k.numericValue)) {
      m[k.key] = k.numericValue;
    }
  }
  return m;
}
